import get from 'lodash/get';

import { ToastTypeVariants } from '@/hooks/useToast';

import { type ToastErrorMessage } from './typings';

export const defaultErrorToastMessage: ToastErrorMessage = (error) => [ToastTypeVariants.ERROR, error.message];

export const getRoutePath = (originalError: unknown): string | null => {
  // Try to get x-glade-path from response headers
  const gladePath = get(originalError, 'response.headers.x-glade-path');
  if (typeof gladePath === 'string') {
    return gladePath;
  }

  // Try to get responseURL from request
  const responseUrl = get(originalError, 'request.responseURL');
  if (typeof responseUrl === 'string') {
    return responseUrl;
  }

  return null;
};

type ApiErrorFingerprintInput = {
  source: 'useNoodleApi' | 'useNoodleSwr';
  errorType: string;
  statusCode: number;
  httpMethod: string | undefined;
  routePath: string | null;
};

/**
 * Build a stable Sentry fingerprint that distinguishes API errors per route.
 *
 * Without this, Sentry groups by the error message produced by `ApiError.create`
 * — which is the backend-returned `message` (e.g. "BadRequest"). That collapses
 * thousands of distinct backend rejections into a single, untriageable issue
 * (DEV-23535 / NOODLE-FRONTEND-G94).
 *
 * Returns `undefined` when there's no route path so Sentry falls back to its
 * default grouping rather than collapsing all routeless errors into one bucket.
 */
export const buildApiErrorFingerprint = ({
  source,
  errorType,
  statusCode,
  httpMethod,
  routePath,
}: ApiErrorFingerprintInput): string[] | undefined => {
  if (!routePath) {
    return undefined;
  }
  return [source, errorType, String(statusCode), httpMethod ?? 'UNKNOWN', routePath];
};
